import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
} from "components";
import { FC, useMemo } from "react";
import {
  useAppLocation,
  useAppNavigate,
  useAppSearch,
} from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import CreateTargetRateContainer from "./create";
import { CurrencyDollarIcon } from "@heroicons/react/24/outline";
import { FetchTargetRates_fetchTargetRates } from "_graphql/queries/__generated__/FetchTargetRates";
import RemoveTargetRateContainer from "./remove";
import Table from "components/tables/table";
import UpdateTargetRateContainer from "./update";
import ViewTargetRateContainer from "./view";
import { usePagination } from "hooks";
import { useTargetRates } from "hooks/data/use-target-rates";
import { withPermissions } from "utils";

type TargetRatesPageProps = {};

const TargetRatesPage: FC<TargetRatesPageProps> = () => {
  const { params } = useAppLocation();
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const { offset, limit, setPage } = usePagination();

  const { targetRates, loading, refetch } = useTargetRates(params?.id || "");

  const length = useMemo(() => targetRates?.length || 0, [targetRates]);

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <>
            {withPermissions(["*:*", "place-it:manage_targets_configuration"])(
              <OfficeButton
                onClick={() =>
                  navigate({
                    search(prev) {
                      return {
                        ...prev,
                        modal: "create",
                      };
                    },
                  })
                }
              >
                Configure Rate
              </OfficeButton>
            )}
          </>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <Table
            title={`Target Rates: ${params?.id || ""}`}
            loading={loading}
            hasSearch
            columns={[
              {
                name: "Rate Details",
                accessor: "rate_value",
                render: (item: FetchTargetRates_fetchTargetRates) => (
                  <div className="flex items-center space-x-2">
                    <CurrencyDollarIcon className="h-6 w-6" />
                    <div className="flex-1 flex flex-col">
                      <span>
                        {item.currency_code || "GHS"} {item.rate_value || 0}
                      </span>
                      <span className="font-light text-xs">
                        {item.currency}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "id",
                render: (item: FetchTargetRates_fetchTargetRates) => (
                  <>
                    <ActionButton
                      action="view"
                      onClick={dispatchAction(item?.id || "", "view")}
                    />
                    {withPermissions([
                      "*:*",
                      "place-it:manage_targets_configuration",
                    ])(
                      <ActionButton
                        action="update"
                        onClick={dispatchAction(item?.id || "", "update")}
                      />
                    )}
                    {withPermissions([
                      "*:*",
                      "place-it:manage_targets_configuration",
                    ])(
                      <ActionButton
                        action="remove"
                        extraClassName="text-red-400"
                        onClick={dispatchAction(item?.id || "", "remove")}
                      />
                    )}
                  </>
                ),
              },
            ]}
            data={targetRates}
            refetch={refetch}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={length}
                currentSize={targetRates.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>

      <CreateTargetRateContainer
        open={searchParams?.modal === "create"}
        setOpen={(value) => {
          navigate({
            search(prev) {
              return {
                ...prev,
                modal: value ? "create" : undefined,
              };
            },
          });
        }}
        targetId={params?.id || ""}
        refetch={refetch}
      />

      {searchParams?.id && (
        <>
          <ViewTargetRateContainer
            open={searchParams?.modal === "view"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "view" : undefined,
                  };
                },
              });
            }}
            rateId={searchParams?.id}
          />

          <UpdateTargetRateContainer
            open={searchParams?.modal === "update"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "update" : undefined,
                  };
                },
              });
            }}
            rateId={searchParams?.id}
          />

          <RemoveTargetRateContainer
            open={searchParams?.modal === "remove"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "remove" : undefined,
                  };
                },
              });
            }}
            rateId={searchParams?.id}
          />
        </>
      )}
    </main>
  );
};

export default TargetRatesPage;
